import { ApolloModule } from 'apollo-angular';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AppStateService } from './shared/services/app-state.service';
import { AuthGuard } from './shared/guards/auth.guard';
import { AuthInterceptor } from './shared/interceptors/authencation.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateTranslatedAdapter } from './shared/adapters/date-translated.adapter';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { GraphQLModule } from './graphql.module';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { HttpErrorInterceptor } from './shared/interceptors/httperror.interceptor';
import { KeywordService } from './shared/services/keyword.service';
import { LogService } from './shared/services/log.service';
import { ModalityService } from './shared/services/modality.service';
import { NavigationModule } from './shared/components/navigation/navigation.module';
import { ModalDialogModule } from './shared/components/modal-dialog/modal-dialog.module';
import { NgModule } from '@angular/core';
import { PatientService } from './shared/services/patient.service';
import { registerLocaleData } from '@angular/common';
import { StudiesService } from './shared/services/studies.service';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserService } from './shared/services/user.service';
import { AdminModalityTypeService } from './components/clinic-details/services/admin-modality-types.service';
import { ClinicTypeService } from './shared/services/clinic-type.service';
import { AdminClinicService } from './components/clinics/services/admin-clinic.service';
import { SerieService } from './shared/services/serie.service';
import { AlbumService } from './shared/services/album.service';
import { InstanceService } from './shared/services/instance.service';
import { AlbumCaseService } from './shared/services/album-case.service';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import { StudyShareService } from './shared/services/study-share.service';
import { ShareStudyModule } from './shared/components/popups/share-study/share-study.module';
import { MailConfigurationService } from './shared/services/mail-configuration.service';
import localeFrExtra from '@angular/common/locales/extra/fr';
import { SccModule } from './scc/scc.module';
import { HttpProgressInterceptor } from './shared/interceptors/http-progress.interceptor';
import { TranslatedPaginator } from './shared/adapters/translated-paginator';
import { SccPopupModule } from './shared/components/popups/scc-popup/scc-popup.module';
import { MAT_CHIPS_DEFAULT_OPTIONS } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { Jodit } from 'jodit';
import 'jodit/esm/plugins/image/image';
import 'jodit/esm/plugins/paste/paste';
import 'jodit/esm/plugins/paste-from-word/paste-from-word';
import 'jodit/esm/plugins/image-properties/image-properties';
import 'jodit/esm/plugins/image-processor/image-processor';
import 'jodit/esm/plugins/clean-html/clean-html';
import 'jodit/esm/plugins/focus/focus';
import 'jodit/esm/plugins/hotkeys/hotkeys';
import 'jodit/esm/plugins/symbols/symbols';
import 'jodit/esm/plugins/search/search';
import 'jodit/esm/plugins/resizer/resizer';
import 'jodit/esm/plugins/select-cells/select-cells';
import 'jodit/esm/plugins/resize-cells/resize-cells';
import 'jodit/esm/plugins/resize-handler/resize-handler';
import 'jodit/esm/plugins/hr/hr';
import 'jodit/esm/plugins/line-height/line-height';
import 'jodit/esm/plugins/indent/indent';
import 'jodit/esm/plugins/select/select';
import 'jodit/esm/plugins/clipboard/clipboard';
import 'jodit/esm/plugins/spellcheck/spellcheck';
import 'jodit/esm/plugins/source/source';
import 'jodit/esm/plugins/preview/preview';
import 'jodit/esm/plugins/print/print';
import 'jodit/esm/plugins/justify/justify';
import 'jodit-pro/esm/plugins/page-break/page-break';
import joditLocaleFr from 'jodit/esm/langs/fr';
import { SccConfirmDataModule } from './shared/components/popups/scc-confirm-data/scc-confirm-data.module';
import { SccDicomDataModule } from './shared/components/popups/scc-dicom-data/scc-dicom-data.module';
import { GlobalNotificationConfirmPopupModule } from './shared/components/popups/global-notification-confirm-popup/global-notification-confirm-popup.module';
import { GlobalNotificationPopupModule } from './shared/components/popups/global-notification-popup/global-notification-popup.module';
import { ClinicNotificationPopupModule } from './shared/components/popups/clinic-notification-popup/clinic-notification-popup.module';
import { UserDetailsPopupModule } from './shared/components/popups/user-details-popup/user-details-popup.module';
import { CguPopupModule } from './shared/components/popups/cgu-popup/cgu-popup.module';
import { ImportantAlertPopupModule } from './shared/components/popups/important-alert-popup/important-alert-popup.module';
import { CreateImportantAlertPopupModule } from './shared/components/popups/create-important-alert-popup/create-important-alert-popup.module';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeFr, 'fr', localeFrExtra);

export const dateLocaleFactory = (translateService: TranslateService) => translateService.getBrowserLang();

// AoT requires an exported function for factories
export const HttpLoaderFactory = (http: HttpClient) => new TranslateHttpLoader(http);

Jodit.lang.fr = joditLocaleFr;

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    AppRoutingModule,
    ApolloModule,
    BrowserAnimationsModule,
    BrowserModule,
    GraphQLModule,
    HttpClientModule,
    MatAutocompleteModule,
    MatSelectModule,
    ModalDialogModule,
    ShareStudyModule,
    SccPopupModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: 'fr',
    }),
    NavigationModule,
    SccModule,
    SccConfirmDataModule,
    SccDicomDataModule,
    GlobalNotificationConfirmPopupModule,
    GlobalNotificationPopupModule,
    ClinicNotificationPopupModule,
    UserDetailsPopupModule,
    CguPopupModule,
    ImportantAlertPopupModule,
    CreateImportantAlertPopupModule
  ],
  providers: [
    // { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: MAT_DATE_LOCALE, useFactory: dateLocaleFactory, deps: [TranslateService] },
    AuthGuard,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true, deps: [LogService] },
    { provide: HTTP_INTERCEPTORS, useClass: HttpProgressInterceptor, multi: true },
    { provide: DateAdapter, useClass: DateTranslatedAdapter, deps: [TranslateService] },
    AlbumService,
    AlbumCaseService,
    SerieService,
    StudiesService,
    KeywordService,
    ModalityService,
    PatientService,
    InstanceService,
    UserService,
    AppStateService,
    AdminModalityTypeService,
    ClinicTypeService,
    AdminClinicService,
    StudyShareService,
    MailConfigurationService,
    TranslateService,
    {
      provide: MAT_CHIPS_DEFAULT_OPTIONS,
      useValue: {
        separatorKeyCodes: [ENTER, COMMA]
      }
    },
    { provide: MatPaginatorIntl, useClass: TranslatedPaginator },
  ],
  exports: [],
  bootstrap: [AppComponent]
})
export class AppModule {
}
